/* eslint-disable jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions,react/no-array-index-key,@typescript-eslint/ban-ts-comment,@typescript-eslint/no-explicit-any */
import React, { useState, useCallback } from "react";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import useCustomizableContext from "~/CustomizableToolPage/useCustomizableContext";
import { PriceGroup } from "~/CustomizableToolPage/types";
import { User } from "~/common/Store/models/User";

interface Props {
  loadingCart: boolean;
  openModal: () => void;
  user: User;
  isTrs: boolean;
  toggleNavigationSidebar: () => void;
  comments: string;
  setComments: (comments: string) => void;
}

const disclaimerText =
  "You represent that you have the right or license to the provided image, likeness, or logo (collectively, “Design”) or the knowledge and consent of the individual having the right to the Design for this reproduction and its use. By placing an order using the Design, you agree to hold Clark Associates, and its related companies, including but not limited to The WEBstaurant Store, LLC, and The Restaurant Store, LLC, and their third-party vendors free and harmless from all liability and to indemnify them for any loss, damage, or injury, including violations of intellectual property rights, that they may suffer as a result of producing and any other use of the Design, including any legal fees and out of pocket costs. At their sole discretion, any Design, deemed inappropriate or obscene by Clark Associates and their third-party vendors may be rejected with both Parties being held harmless for the rejection.";

const PricePanel = ({
  openModal,
  user,
  toggleNavigationSidebar,
  isTrs,
  comments,
  setComments,
}: Props) => {
  const {
    loadingCart,
    productData,
    // @ts-ignore
    getCurrentPriceGroups,
    // @ts-ignore
    getCurrentPriceGroupSortedKeys,
    // @ts-ignore
    getSpecialQuantityValues,
    // @ts-ignore
    getCustomizationDescriptions,
    // @ts-ignore
    getCurrentPriceGroupMaxQuantity,
    // @ts-ignore
    getCurrentPriceGroupMinimumBuy,
    // @ts-ignore
    getMinMustBuyText,
    // @ts-ignore
    generateAllRegionDesigns,
    // @ts-ignore
    getCustomizationDataToSave,
    // @ts-ignore
    setCartLoading,
  } = useCustomizableContext();

  const [legalConfirmed, setLegalConfirmed] = useState(false);
  const [shouldShowLegalRequired, setShouldShowLegalRequired] = useState(false);
  const [quantityInputMessage, setQuantityInputMessage] = useState<
    string | null
  >(null);
  const [switchedToInput, setSwitchedToInput] = useState(false);
  const [productQuantity, setProductQuantity] = useState<number>(
    (() => {
      const minBuy = getCurrentPriceGroupMinimumBuy();
      if (minBuy > 1) {
        return minBuy;
      }
      if (productData.mustBuy > 1) {
        return productData.mustBuy;
      }
      return 1;
    })()
  );

  const currentPriceGroups = getCurrentPriceGroups();
  const currentPriceGroupSortedKeys = getCurrentPriceGroupSortedKeys();

  const productHasMinOrMustBuy =
    productData.minimumBuy > 1 ||
    productData.mustBuy > 1 ||
    getSpecialQuantityValues().length;
  const minMustBuyText = getMinMustBuyText();

  const getAddToCartText = (cartLoading: boolean) =>
    cartLoading ? "Adding to cart..." : "Add to Cart";

  const handleCustomizationCommentsChanged = ({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement>) => setComments(value);
  const handleLegalConfirmationChecked = () =>
    setLegalConfirmed(!legalConfirmed);

  const handleUpdateQty:
    | React.ChangeEventHandler<HTMLSelectElement>
    | React.ChangeEventHandler<HTMLInputElement> = useCallback(
    // @ts-ignore
    ({ target: { value } }) => {
      const qty = value !== "Other" && Math.floor(Number(value));
      const currentPriceGroupMinimumBuy = getCurrentPriceGroupMinimumBuy();

      if (qty) {
        const maxQuantity = getCurrentPriceGroupMaxQuantity();
        let quantity = qty;

        if (quantity > maxQuantity) {
          quantity = maxQuantity;
          setProductQuantity(quantity);
        }

        if (quantity < 1) {
          setQuantityInputMessage("Please enter a quantity of more than 0.");
        } else if (
          currentPriceGroupMinimumBuy > 1 &&
          quantity < currentPriceGroupMinimumBuy
        ) {
          setQuantityInputMessage(
            `Please enter a quantity of more than ${currentPriceGroupMinimumBuy}.`
          );
        } else if (
          productData.mustBuy > 1 &&
          // eslint-disable-next-line eqeqeq
          !(quantity % productData.mustBuy == 0)
        ) {
          setQuantityInputMessage(
            `Please enter a quantity as a multiple of ${productData.mustBuy}.`
          );
        } else {
          setQuantityInputMessage(null);
        }

        setProductQuantity(qty);
      } else if (!switchedToInput) {
        setSwitchedToInput(true);
      } else {
        setProductQuantity(0);
      }
    },
    [
      getCurrentPriceGroupMaxQuantity,
      getCurrentPriceGroupMinimumBuy,
      productData.mustBuy,
      switchedToInput,
    ]
  );

  const { totalPrice, additionalFee, priceGroupGuid } = (() => {
    const priceGroups: Lookup<PriceGroup> = getCurrentPriceGroups() || {};

    let totalPrice = 0.0;
    let additionalFee = 0.0;
    let priceGroupGuid = "";

    Object.values(priceGroups).forEach((priceGroup) => {
      if (
        productQuantity >= priceGroup.startQuantity &&
        productQuantity <= priceGroup.endQuantity
      ) {
        const preAdjustedTotal =
          priceGroup.price * productQuantity + priceGroup.additionalFee;
        const adjustedUnitPrice =
          Math.round((preAdjustedTotal / productQuantity) * 100) / 100;

        totalPrice = adjustedUnitPrice * productQuantity;
        additionalFee = priceGroup.additionalFee;
        priceGroupGuid = priceGroup.priceGroupGuid;
        // eslint-disable-next-line no-useless-return
        return;
      }
    });

    return { totalPrice, additionalFee, priceGroupGuid };
  })();

  const createCustomizedProduct = useCallback(
    async (payload: any[], user: User) => {
      const content: any = {
        product: {
          ...payload[0],
          stateData: [
            ...payload[0].stateData.map((sd: any) => ({
              ...sd,
              objectJSON: JSON.stringify(sd.objectJSON),
              clientIdentifier: sd.objectJSON.clientIdentifier,
            })),
          ],
        },
      };

      if (user && user.index !== "0") {
        content.userId = user.index;
      } else if (user) {
        content.sessionId = user.sessionId;
      }

      const response = await fetch(
        "/customized-products/createcustomizedproductsforcart/",
        {
          method: "POST",
          body: JSON.stringify(content),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      // eslint-disable-next-line no-return-await
      try {
        const json = await response.json<any>();
        return json;
      } catch {
        setCartLoading(false);
      }
      return null;
    },
    [setCartLoading]
  );

  const handleAddToCart = useCallback(async () => {
    if (!legalConfirmed) {
      setShouldShowLegalRequired(true);
      return;
    }

    if (!quantityInputMessage && totalPrice > 0) {
      setCartLoading(true);

      const designsArray = await generateAllRegionDesigns();

      // the designsArray will contain each arrays region design as a base64 image
      // lets assign the designsArrays to all products
      const customizationData = getCustomizationDataToSave();

      Object.values(customizationData).forEach((product) => {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        product.priceGroupGUID = priceGroupGuid;
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        product.regionalCanvasImages = designsArray;
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        product.comments = comments;
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        product.quantity = productQuantity;
      });

      const response = await createCustomizedProduct(customizationData, user);

      if (response) {
        const formData = {
          from: "add",
          item_number: response.itemNumber,
          weight: response.weight,
          isHistoryClicked: response.isHistoryClicked,
          price: response.price,
          pricetoolow: response.priceTooLow,
          description: response.description,
          suffix: response.suffix,
          uom: response.uom,
          qty: response.quantity,
          shipping: response.shipping,
          // ups_ship_type: "M",
          ds: response.ds,
          group: response.group,
          cost: response.cost,
          taxcode: response.taxcode,
          category: response.category,
        };

        const form = document.createElement("form");
        // @ts-ignore
        form.style = "display:none";

        document.body.appendChild(form);
        form.action = "/viewcart.cfm";
        form.method = "post";

        if (isTrs) {
          window.parent.postMessage(JSON.stringify(formData), "*");
        } else {
          Object.keys(formData).forEach((item) => {
            const input = document.createElement("input");
            input.name = item;
            // @ts-ignore
            input.value = formData[item];
            form.appendChild(input);
          });
          form.submit();
        }
      } else {
        setQuantityInputMessage(
          "An error occurred while saving customization data"
        );
        setCartLoading(false);
      }
    }
  }, [
    comments,
    createCustomizedProduct,
    generateAllRegionDesigns,
    getCustomizationDataToSave,
    isTrs,
    legalConfirmed,
    priceGroupGuid,
    productQuantity,
    quantityInputMessage,
    setCartLoading,
    totalPrice,
    user,
  ]);

  return (
    <div id="price-panel">
      <span
        className="icon-list customize__menu-icon"
        onClick={toggleNavigationSidebar}
      >
        Options
      </span>
      <div className="customize__pricing-title">Your Customizations:</div>
      <table className="customize__pricing-info">
        <thead>
          <tr>
            <th className="customize__pricing-head">Location</th>
            <th className="customize__pricing-head">Customization Type</th>
          </tr>
        </thead>
        <tbody>
          {/* @ts-ignore */}
          {Object.values(getCustomizationDescriptions()).map(
            (customization: any, index) => (
              <tr key={index}>
                <td>
                  <strong className="customize__pricing-bold">
                    {customization.locationName}
                  </strong>
                </td>
                <td>
                  {customization.type}
                  {!!customization.details.length && <br />}
                  {customization.additionalText.map(
                    (text: string, index: number) =>
                      text.length && (
                        <span key={`${index}-text`}>
                          <strong className="customize__pricing-bold">
                            {text}
                          </strong>
                          {!(
                            !customization.details.length &&
                            index === customization.additionalText.length - 1
                          ) && <br />}
                        </span>
                      )
                  )}
                  {customization.details.map(
                    (detail: string, index: number) =>
                      // eslint-disable-next-line eqeqeq
                      detail != "default: " && (
                        <span
                          className="customize__pricing-gray"
                          key={`${index}-detail`}
                        >
                          {detail}
                        </span>
                      )
                  )}
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>

      <form className="form customize__pricing-form">
        <textarea
          className="customize__pricing-textfield form__control"
          rows={4}
          maxLength={500}
          placeholder="Please indicate any further details, such as a need-by date or specifications for your custom item."
          value={comments}
          disabled={loadingCart}
          onChange={handleCustomizationCommentsChanged}
        />

        <div className="customize__qty-container">
          {currentPriceGroupSortedKeys.map((price: string) => {
            const priceGroup = currentPriceGroups[price];
            return (
              <div className="customize__qty-row" key={priceGroup.id}>
                {(priceGroup.endQuantity === priceGroup.startQuantity && (
                  <div className="customize__qty-cell customize__qty">
                    Buy {priceGroup.startQuantity}
                  </div>
                )) ||
                  (priceGroup.endQuantity < 9999 && (
                    <div className="customize__qty-cell customize__qty">
                      Buy {priceGroup.startQuantity} - {priceGroup.endQuantity}
                    </div>
                  )) || (
                    <div className="customize__qty-cell customize__qty">
                      Buy {priceGroup.startQuantity}+
                    </div>
                  )}
                <div className="customize__qty-cell customize__qty-price pricing">
                  <div className="price">
                    {formatAsCurrency.formatIfUndefined(
                      priceGroup.price,
                      true,
                      false,
                      { minimumFractionDigits: 2 }
                    )}
                    <span className="each">/{priceGroup.uom}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="customize__pricing-disclaimer">
          {(additionalFee > 0 && (
            <>
              Note - This item incurs a fee of&nbsp;
              {formatAsCurrency.formatIfUndefined(additionalFee, true, false, {
                minimumFractionDigits: 2,
              })}
              . View other&nbsp;
            </>
          )) || <>View&nbsp;</>}
          <a href="#" onClick={openModal} data-toggle="modal">
            Design Considerations
          </a>
          {!!productData.priceDisclaimer.length && (
            <>
              <br />
              {productData.priceDisclaimer}
            </>
          )}
        </div>

        <div className="ag-product">
          <div className="add-to-cart">
            <p className="customize__legal_blurb">
              <input
                type="checkbox"
                onChange={handleLegalConfirmationChecked}
                checked={legalConfirmed}
              />
              &nbsp;{disclaimerText}
            </p>
            {shouldShowLegalRequired && (
              <span className="text-red-500 leading-4 left-0 bottom-2-1/2 text-xs">
                Please accept terms and conditions
              </span>
            )}
            <span>
              {!!minMustBuyText.length && (
                <p className="min-must-text">{minMustBuyText}</p>
              )}
              <div
                className={`btn-container${loadingCart ? " disabled" : ""}${
                  productHasMinOrMustBuy && !switchedToInput
                    ? " special-quantity qty-preset"
                    : ""
                }`}
              >
                {(productHasMinOrMustBuy && !switchedToInput && (
                  <select
                    className="configure qty-preset-select"
                    value={productQuantity}
                    // @ts-ignore
                    onChange={handleUpdateQty}
                  >
                    <option disabled selected value="0">
                      Select Quantity
                    </option>
                    {(getSpecialQuantityValues() || []).map(
                      (val: string, index: number) => (
                        <option value={val} key={index}>
                          {val}
                        </option>
                      )
                    )}
                  </select>
                )) || (
                  <input
                    style={{ boxSizing: "initial", width: "52px" }}
                    type="number"
                    className="quantity"
                    value={productQuantity}
                    // @ts-ignore
                    onChange={handleUpdateQty}
                  />
                )}

                <input
                  type="button"
                  onClick={handleAddToCart}
                  disabled={loadingCart || totalPrice <= 0}
                  className={`btn btn-cart btn-large${
                    loadingCart ? " disabled" : ""
                  }`}
                  value={getAddToCartText(loadingCart)}
                />
              </div>
              {quantityInputMessage && (
                <p className="messages">{quantityInputMessage}</p>
              )}
            </span>
          </div>
        </div>

        <div className="customize__total pricing">
          <span className="customize__total-text">Total:</span>
          <span className="price">
            {formatAsCurrency.formatIfUndefined(totalPrice, true, false, {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className="customize__total--disclaimer">
          Customized products are subject to extended lead times based on
          manufacturer production capacity. Additional setup fees may apply.
        </div>
      </form>
    </div>
  );
};

export default PricePanel;
